import { motion } from "framer-motion";
import { Link as WaspRouterLink, routes } from "wasp/client/router";
import { Link as ReactRouterLink } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";
import { trackLandingCta } from "../../client/utils/posthog";
import FloatingDashboard from "./FloatingDashboard";

/**
 * Section 1 — Hero (Homepage V2).
 *
 * Two-column split: text left, floating-dashboard composition right (stacks to
 * text-then-visual on mobile). The landing is locked to Classic Dark (see
 * App.tsx), so this uses the dark token palette. SEO: one native <h1>, <p>
 * subtitle, real <a> pills; the right column is a decorative role="img"
 * composition (see FloatingDashboard).
 */
export default function Hero() {
  const t = useTranslation();
  const h = t.marketing.hero;

  return (
    <section className="relative overflow-hidden mx-[calc(-50vw+50%)] w-screen">
      {/* HUD grid background */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,201,167,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,201,167,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
        }}
      />
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/4 h-[500px] w-[600px] -translate-x-1/2 rounded-full bg-primary/[0.07] blur-[120px]" />
        <div className="absolute top-1/3 -right-40 h-[400px] w-[400px] rounded-full bg-primary/[0.05] blur-[100px]" />
      </div>

      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-5 pt-24 pb-12 sm:px-6 md:gap-8 md:pt-28 md:pb-16 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:pt-32 lg:pb-20">
        {/* ── Left column: text ── */}
        <div className="order-1 flex flex-col justify-center md:pr-8 lg:pr-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-[32px] font-extrabold leading-[1.1] tracking-tight text-foreground md:text-[40px] lg:text-[56px]"
            style={{ textWrap: "balance" }}
          >
            {h.titleBeforeHighlight}
            <span className="text-primary">{h.titleHighlight}</span>
            {h.titleAfterHighlight}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 max-w-[480px] text-base leading-relaxed text-muted-foreground md:text-lg"
          >
            {h.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-10"
          >
            <WaspRouterLink
              to={routes.SignupRoute.to}
              onClick={() => trackLandingCta("hero", "free")}
              className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] px-6 py-3.5 text-[15px] font-semibold transition-colors duration-150 hover:brightness-105 sm:w-auto sm:px-7 sm:text-base"
              style={{ backgroundColor: "#00C9A7", color: "#0F1729" }}
            >
              {h.cta}
              <ArrowRight className="h-4 w-4" />
            </WaspRouterLink>

            <p className="mt-3 text-center text-[13px] text-muted-foreground/80 sm:text-left">
              {h.microcopy}
            </p>
          </motion.div>

          {/* ── Feature pills ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mt-8 lg:mt-12"
          >
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/60">
              {h.pillsLabel}
            </p>
            <div className="no-scrollbar flex gap-2 overflow-x-auto pb-2 lg:flex-wrap lg:overflow-visible lg:pb-0">
              {h.pills.map((pill) => (
                <ReactRouterLink
                  key={pill.label + pill.to}
                  to={pill.to}
                  className="shrink-0 whitespace-nowrap rounded-lg border border-border bg-card px-3.5 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors duration-150 hover:border-primary/60 hover:bg-card-accent hover:text-primary"
                >
                  {pill.label}
                </ReactRouterLink>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── Right column: floating dashboard composition ──
            On lg+ the tilted mockup is allowed to bleed slightly past the grid
            column (negative right margin + wider max) for a "product" feel. */}
        <div className="order-2 flex items-center justify-center lg:justify-end">
          <div className="w-full lg:max-w-none lg:-mr-6 xl:-mr-12 lg:w-[640px] xl:w-[720px]">
            <FloatingDashboard />
          </div>
        </div>
      </div>
    </section>
  );
}
